import { createSelector, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import {
  PATIENTS_LIST_KEY_DEFAULT,
  QUICK_FILTER_DEFAULT_TYPE,
} from "constants/patients";
import { getSelectedIds } from "utils/common";

interface FilterState {
  quickFilterCode: string;
  searchString: string;
  selectedIds: Record<number, boolean>;
  openActionModal: boolean;
  showCheckboxes: boolean;
}

const DEFAULT_FILTER_STATE = {
  quickFilterCode: "",
  searchString: "",
  selectedIds: {},
  openActionModal: false,
  showCheckboxes: false,
};

interface FiltersState {
  lists: {
    [key: string]: FilterState;
  };
}

const initialState = {
  lists: {
    [PATIENTS_LIST_KEY_DEFAULT]: {
      ...DEFAULT_FILTER_STATE,
      quickFilterCode: String(QUICK_FILTER_DEFAULT_TYPE),
    },
  },
} as FiltersState;

const filtersSlice = createSlice({
  name: "filters",
  initialState,
  reducers: {
    applySearchStringFilter(
      state,
      action: PayloadAction<{ key: string; searchString: string }>
    ) {
      const { key, searchString } = action.payload;

      if (!state.lists[key]) {
        state.lists[key] = { ...DEFAULT_FILTER_STATE };
      }

      state.lists[key].searchString = searchString.trim();
    },
    applyQuickFilter(
      state,
      action: PayloadAction<{ key: string; quickFilterCode: string }>
    ) {
      const { key, quickFilterCode } = action.payload;

      if (!state.lists[key]) {
        state.lists[key] = { ...DEFAULT_FILTER_STATE };
      }

      state.lists[key].quickFilterCode = quickFilterCode;
    },
    addSelectedId: (
      state,
      action: PayloadAction<{ key: string; id: number; selected: boolean }>
    ) => {
      const { key, id, selected } = action.payload;
      if (!state.lists[key]) {
        state.lists[key] = { ...DEFAULT_FILTER_STATE };
      }
      state.lists[key].selectedIds[id] = selected;
    },
    bulkSelectedId: (
      state,
      action: PayloadAction<{ key: string; ids: number[] }>
    ) => {
      const { key, ids } = action.payload;
      if (!state.lists[key]) {
        state.lists[key] = { ...DEFAULT_FILTER_STATE };
      }
      state.lists[key].selectedIds = {};
      ids.forEach((id) => {
        state.lists[key].selectedIds[id] = true;
      });
    },
    toggleOpenActionModal(state, action: PayloadAction<{ key: string }>) {
      const { key } = action.payload;
      if (!state.lists[key]) {
        state.lists[key] = { ...DEFAULT_FILTER_STATE };
      }
      state.lists[key].openActionModal = !state.lists[key].openActionModal;
    },
    toggleShowCheckboxes: (state, action: PayloadAction<{ key: string }>) => {
      const { key } = action.payload;
      if (!state.lists[key]) {
        state.lists[key] = { ...DEFAULT_FILTER_STATE };
      }
      const newState = !state.lists[key].showCheckboxes;
      state.lists[key].showCheckboxes = newState;

      if (!newState) {
        state.lists[key].selectedIds = {};
      }
    },
  },
});

const selectQuickFilterCodeValue = (state: RootState, key: string) =>
  state.filters.lists[key]?.quickFilterCode || "";

const selectSearchStringFilterValue = (state: RootState, key: string) =>
  state.filters.lists[key]?.searchString || "";

export const selectSelectedIds = (state: RootState, key: string) =>
  state.filters.lists[key]?.selectedIds || {};

export const selectSelectedCount = (state: RootState, key: string) =>
  getSelectedIds(state.filters.lists[key]?.selectedIds || {}).length;

export const selectOpenActionModal = (state: RootState, key: string) =>
  state.filters.lists[key]?.openActionModal || false;

export const selectShowCheckboxes = (state: RootState, key: string) =>
  state.filters.lists[key]?.showCheckboxes || false;

// We create a factory function for our selector which takes the key as an argument
const makeSelectFilterValues = () => {
  return createSelector(
    selectQuickFilterCodeValue,
    selectSearchStringFilterValue,
    selectShowCheckboxes,
    selectOpenActionModal,
    selectSelectedIds,
    selectSelectedCount,
    (
      quickFilterCodeValue,
      searchStringFilterValue,
      showCheckboxes,
      openActionModal,
      selectedIdsMap, // This is the selectedIds for the current key
      selectedCount
    ) => {
      return {
        quickFilterCode: quickFilterCodeValue,
        searchString: searchStringFilterValue,
        showCheckboxes,
        openActionModal,
        selectedIdsMap,
        selectedCount,
      };
    }
  );
};

// Here's our selector cache
const selectorCache: {
  [key: string]: ReturnType<typeof makeSelectFilterValues>;
} = {};

export const selectFilterValues = (key: string) => {
  if (!selectorCache[key]) {
    selectorCache[key] = makeSelectFilterValues();
  }
  return selectorCache[key];
};

export const {
  applyQuickFilter,
  applySearchStringFilter,
  bulkSelectedId,
  addSelectedId,
  toggleOpenActionModal,
  toggleShowCheckboxes,
} = filtersSlice.actions;

export default filtersSlice.reducer;
